import React, { Suspense, useEffect, useRef, useState } from "react";
import { ETypeOfNotification, Loader, LoginForm, LoginResponse, setNotification, WindowLoader, SETTINGS_VERSION } from "medere-ui-framework";
import { useLoginView } from "./UseLoginView";
import "./LoginView.scss";
import { authenticateUser } from "../../services/authService";
import { HttpStatusCode } from "axios";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { setPatientDocument, setPatientDocumentType } from "../../redux/slices/ValidationViewSlice";
import { extractPatientIdentifications } from "../../services/digitalRPservice";
import { MedereLoginResponse } from "../../interface/MedereLoginResponse";
import ConsentWindow from "./ConsentWindow";
import DOMPurify from "dompurify";
import { acceptConsent, getSiteConsent, getSiteLogo } from "../../services/medereSiteController";

type LoginViewProps = {
  children?: React.ReactNode;
  data?: string;
};

type LoginSettings = {
  loginResponse: LoginResponse;
  rememberMe: boolean;
  settingsVersion: string;
  withNavigation: boolean;
};

export default function LoginView({ children }: Readonly<LoginViewProps>) {
  const [searchParams] = useSearchParams();
  const data = searchParams.get("data");
  const hasAuthenticated = useRef(false);
  const navigate = useNavigate();
  const { settingUserMedereentity, welcomeDesc } = useLoginView();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showConsent, setShowConsent] = useState<boolean>(false);
  const [consentHTML, setConsentHTML] = useState<string | null>(null);
  const [loginSettings, setLoginSettings] = useState<LoginSettings | null>(null);

  // Fetch the logo when the component mounts
  useEffect(() => {
    getSiteLogo().then((url) => setLogoUrl(url));
  }, []);

  useEffect(() => {
    if (hasAuthenticated.current || !data) {
      setIsLoading(false);
      return;
    }

    hasAuthenticated.current = true; //hace que solo haga el metodo authenticateUser una sola vez

    const authenticate = async () => {
      try {
        const res = await authenticateUser(data);
        if (res.status === HttpStatusCode.Ok) {
          let loginResponse: LoginResponse = res.data;
          settingUserMedereentity(loginResponse, true, SETTINGS_VERSION, false);

          const response:any = await extractPatientIdentifications(data)
          dispatch(setPatientDocumentType(response.documenttype))
          dispatch(setPatientDocument(response.document))
          
          navigate("/recetas", { replace: true });
        } else {
          throw new Error("Acceso incorrecto");
        }
      } catch (error) {
        dispatch(
          setNotification({
            desc: "Acceso incorrecto",
            icon: "pi-info-circle",
            typeOfNotification: ETypeOfNotification.ERROR,
          })
        );
        localStorage.removeItem("sessionExpired");
      } finally {
        setIsLoading(false);
      }
    };

    authenticate();
  }, [data]);

  function setSettings() {
    if(loginSettings === null) return; 

    settingUserMedereentity(loginSettings.loginResponse, loginSettings.rememberMe, loginSettings.settingsVersion, loginSettings.withNavigation);
    acceptConsent();
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <WindowLoader showLoader/>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="loginContainer flexible--column">
        <LoginForm
          className="loginForm"
          title={JSON.parse(localStorage.getItem("siteSettings") ?? "{}").appTitle}
          welcomeDesc={welcomeDesc}
          onAuthenticate={(res, rememberMe, settingsVersion) => {
            const loginResponse = res as MedereLoginResponse;
            if (loginResponse.showConsentWindow) {
              setLoginSettings({loginResponse:res, rememberMe, settingsVersion,withNavigation: true});
              if(!consentHTML)
                getSiteConsent().then((consent) => setConsentHTML(DOMPurify.sanitize(consent)));
              setShowConsent(true);
              return;
            }
            settingUserMedereentity(res, rememberMe, settingsVersion, true)
          }}
        >
          {children}
        </LoginForm>
        <div className="logoContainer">
          <img src={"imgs/advenio.png"} alt="Logo" className="logoImage" width={"100px"} height={"100px"} />
        </div>
      </div>
      <ConsentWindow showConsentWindow={showConsent} setShowConsentWindow={setShowConsent} consentHtml={consentHTML} onAccept={() => setSettings()}  />
    </React.Fragment>
  );
}
